import React, { useMemo } from 'react'

import isURL from 'validator/lib/isURL'
import { IExternalIntegrationForm } from 'models/integrations'
import { SynchronousCourseTypes } from 'enum'
import { CreateNewCourseForm, GetValue } from './types'
import { defaultValues, defaultErrors } from './common'

type Error = string

type Errors = {
  [P in keyof CreateNewCourseForm]?: Error
} & { integrationsExternalLink?: Error }

export const FormContext = React.createContext<{
  values: CreateNewCourseForm
  setFieldValue: <F extends keyof CreateNewCourseForm>(
    fieldName: F,
    value: GetValue<F>
  ) => void
  setValues: React.Dispatch<React.SetStateAction<CreateNewCourseForm>>
  errors: Errors
  setErrors: React.Dispatch<React.SetStateAction<Errors>>
  setError: (fieldName: keyof CreateNewCourseForm, error?: Error) => void
  invalidFields: {
    synchronousUrl: boolean
    synchronousLocation: boolean
  }
  valid: boolean
  invalid: boolean
}>(Object.create(null))
export const FormConsumer = FormContext.Consumer
export const useForm = () => React.useContext(FormContext)

export const useField = <Name extends keyof CreateNewCourseForm>(
  name: Name
) => {
  const { values, errors, setFieldValue, setError } = useForm()
  const value = values[name]
  const error = errors[name]
  const field = React.useMemo(
    () => ({
      value,
      error,
      change: (value: GetValue<Name>) => setFieldValue(name, value),
      setError: (error?: Error) => setError(name, error),
    }),
    [value, error, setError, setFieldValue, name]
  )
  return field
}

export const useFieldExternalIntegration = () => {
  const integrations = useField('integrations')
  const external = integrations.value?.external

  const field = React.useMemo(
    () => ({
      value: external,
      change: (value: IExternalIntegrationForm) =>
        integrations.change({ ...integrations.value, external: value }),
    }),
    [external, integrations]
  )
  return field
}

export const FormProvider: React.FC = ({ children }) => {
  const [values, setValues] = React.useState(defaultValues)

  const [errors, setErrors] = React.useState<Errors>(defaultErrors)

  const setFieldValue = <F extends keyof CreateNewCourseForm>(
    fieldName: F,
    value: GetValue<F>
  ) => {
    setValues(() => ({ ...values, [fieldName]: value }))
  }

  const setError = <F extends keyof CreateNewCourseForm>(
    fieldName: F,
    value?: string | null
  ) => {
    setErrors((state) => ({ ...state, [fieldName]: value }))
  }

  const invalidFields = useMemo(() => {
    const { courseExternal, courseSynchronous, courseTypeSynchronous } = values
    let synchronousUrl = false
    let synchronousLocation = false

    if (courseSynchronous) {
      switch (courseTypeSynchronous) {
        case SynchronousCourseTypes.external:
          synchronousUrl = !courseExternal || !isURL(courseExternal)
          break
        case SynchronousCourseTypes.faceToFace:
          synchronousLocation = !courseExternal?.trim()
          break
      }
    }
    return {
      fields: {
        synchronousUrl,
        synchronousLocation,
      },
      invalid: synchronousUrl || synchronousLocation,
    }
  }, [values])

  return (
    <FormContext.Provider
      value={{
        values,
        setFieldValue,
        setValues,
        errors,
        setErrors,
        setError,
        invalidFields: invalidFields.fields,
        invalid: invalidFields.invalid,
        valid: !invalidFields.invalid,
      }}
    >
      {children}
    </FormContext.Provider>
  )
}
